import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { fetchJson } from '../lib/http';
import { useSession } from '../state/SessionContext';
import Navbar from '../../components/marketing/Navbar';

export function Layout({ children }: { children: React.ReactNode }) {
	const location = useLocation();
	const navigate = useNavigate();
	const { session, setSession } = useSession();
	const [activeBusinessName, setActiveBusinessName] = React.useState<string>('');

	React.useEffect(() => {
		if (!session?.businessId || !session?.customerId) { setActiveBusinessName(''); return; }
		(async () => {
			try {
				const links = await fetchJson<any[]>('/portal/contexts');
				const match = Array.isArray(links) ? links.find(l => l.businessId === session.businessId && l.customerId === session.customerId) : null;
				setActiveBusinessName(match?.businessName || session.businessId);
			} catch {
				setActiveBusinessName(session.businessId || '');
			}
		})();
	}, [session?.businessId, session?.customerId]);

	async function onLogout() {
		try {
			await fetchJson('/portal/logout', { method: 'POST' });
		} catch {}
		setSession(null);
		navigate('/custportal/login', { replace: true });
	}

	const NavLink = ({ to, children: label }: { to: string; children: React.ReactNode }) => (
		<Link to={to} className={
			`px-3 py-2 rounded-lg text-sm font-semibold ${location.pathname.startsWith(to) ? 'bg-sedra/10 text-sedra ring-1 ring-sedra/20' : 'text-gray-700 hover:bg-gray-50'}`
		}>
			{label}
		</Link>
	);

	const isLoggedIn = Boolean(session?.customerId);

	return (
		<div className="min-h-screen">
			{/* Top bar: show portal nav only when logged in; otherwise use marketing navbar like homepage */}
			{isLoggedIn ? (
				<header className="sticky top-0 z-40 bg-white/70 backdrop-blur border-b border-gray-100">
					<div className="mx-auto max-w-7xl px-6 lg:px-8 h-16 flex items-center justify-between">
						<div className="flex items-center gap-4">
							<span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-sedra/10 text-sedra">◎</span>
							<span className="brand-invoice text-xl font-bold">Customer Portal</span>
							{/* Active business indicator */}
							{(session?.businessId || activeBusinessName) && (
								<div className="hidden md:flex">
									<span className="inline-flex items-center gap-2 rounded-xl bg-sedra/10 ring-1 ring-sedra/20 px-3 py-1">
										<span className="text-xs font-semibold uppercase tracking-wide text-sedra">Active</span>
										<span className="text-sm font-semibold text-gray-900">{activeBusinessName || session?.businessId}</span>
									</span>
								</div>
							)}
							<nav className="ml-4 hidden sm:flex items-center gap-2">
								<Link to="/custportal/contexts" className="px-3 py-2 rounded-lg text-sm font-semibold text-white bg-sedra hover:bg-sedra-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra">Change Business</Link>
								<NavLink to="/custportal/dashboard">Dashboard</NavLink>
							</nav>
						</div>
						<div className="flex items-center gap-3">
							{session?.customerId && (
								<button onClick={onLogout} className="rounded-xl border border-sedra text-sedra px-4 py-2.5 text-sm font-semibold hover:bg-sedra/5 focus-visible:ring-2 focus-visible:ring-sedra focus-visible:ring-offset-2">
									Logout
								</button>
							)}
						</div>
					</div>
				</header>
			) : (
				<Navbar />
			)}
			<main className="mx-auto max-w-7xl px-6 lg:px-8 py-8">
				{children}
			</main>
		</div>
	);
} 